import NetworkIcon from "@core/network-icon";
import { AITimelineContent } from "../../types/ai-templates";
import { motion } from "framer-motion";
import { useState } from "react";

interface TimelineTemplate3Props {
  content: AITimelineContent;
  onUpdate: (newContent: AITimelineContent) => void;
}

export function TimelineTemplate3({
  content,
  onUpdate,
}: TimelineTemplate3Props) {
  console.log("TimelineTemplate3 content:", content);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState("");

  // Handle event update
  const handleEventUpdate = (
    index: number,
    data: { title?: string; description?: string },
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[index]) {
      newContent.steps[index] = {
        ...newContent.steps[index],
        ...data,
      };
      onUpdate(newContent);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  // Ensure events array exists and has at least 3 events to match SVG
  if (!content.steps || content.steps.length < 3) {
    return (
      <div className="p-4 text-center text-gray-500">
        At least 3 timeline events are required to display.
      </div>
    );
  }

  // Define positions and styles for icons, titles, descriptions, and shapes
  const eventData = [
    {
      iconTransform: "translate(386, 282)",
      textTransform: "translate(380, 600)",
      descTransform: "translate(380, 600)",
      iconStroke:
        content.steps[0].color ||
        content.genericColors[0 % content.genericColors.length] ||
        "#6D4C41",
      iconStrokeOverlay: `${content.steps[0].color || content.genericColors[0 % content.genericColors.length] || "#6D4C41"}80`,
      fill:
        content.steps[0].color ||
        content.genericColors[0 % content.genericColors.length] ||
        "#6D4C41",
      fillOverlay: "url(#crawlingPattern-818f641a)",
      index: 0,
      shapeFill: "#E8D9D4",
      shapeFillOverlay: "url(#crawlingPattern-818f641a)",
      shapeTransform: "translate(386, 282)",
      descColor: "#4B3C34",
    },
    {
      iconTransform: "translate(218, 281.94140625)",
      textTransform: "translate(212, 600)",
      descTransform: "translate(212, 600)",
      iconStroke:
        content.steps[1].color ||
        content.genericColors[1 % content.genericColors.length] ||
        "#795548",
      iconStrokeOverlay: `${content.steps[1].color || content.genericColors[1 % content.genericColors.length] || "#795548"}80`,
      fill:
        content.steps[1].color ||
        content.genericColors[1 % content.genericColors.length] ||
        "#795548",
      fillOverlay: "url(#crawlingPattern-5a87971a)",
      index: 1,
      shapeFill: "#EBDDD6",
      shapeFillOverlay: "url(#crawlingPattern-5a87971a)",
      shapeTransform: "translate(218, 281.94140625)",
      descColor: "#553E36",
    },
    {
      iconTransform: "translate(50, 281.94140625)",
      textTransform: "translate(44, 600)",
      descTransform: "translate(44, 600)",
      iconStroke:
        content.steps[2].color ||
        content.genericColors[2 % content.genericColors.length] ||
        "#8D6E63",
      iconStrokeOverlay: `${content.steps[2].color || content.genericColors[2 % content.genericColors.length] || "#8D6E63"}80`,
      fill:
        content.steps[2].color ||
        content.genericColors[2 % content.genericColors.length] ||
        "#8D6E63",
      fillOverlay: "url(#crawlingPattern-65927c1a)",
      index: 2,
      shapeFill: "#EFE2DD",
      shapeFillOverlay: "url(#crawlingPattern-65927c1a)",
      shapeTransform: "translate(50, 281.94140625)",
      descColor: "#634C45",
    },
  ];

  // Handle extra events (fourth and fifth)
  const extraEvents = content.steps.slice(3).map((event, index) => ({
    index: 3 + index,
    title: event.title,
    description: event.description,
    iconId: event.iconId,
    iconDescription: event.icon_description,
    color:
      event.color ||
      content.genericColors[(3 + index) % content.genericColors.length] ||
      "#A1887F",
    descColor: index === 0 ? "#715E58" : "#836F6B",
  }));

  return (
    <div className="p-6 overflow-hidden">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-max mx-auto"
      >
        <svg
          width="576"
          height="708"
          viewBox="0 0 576 708"
          style={{
            fill: "none",
            stroke: "none",
            fillRule: "evenodd",
            clipRule: "evenodd",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeMiterlimit: 1.5,
          }}
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <style>
            {`
              @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=block');
            `}
          </style>
          <defs>
            <pattern
              id="crawlingPattern-8787871a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#8787871a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <symbol id="crawlingSymbol">
              <g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  from="0 0"
                  to="-12 0"
                  dur="0.4s"
                  repeatCount="indefinite"
                ></animateTransform>
                <path d="M -12.5 12.5 l 13 -13 l 6 0 l -13 13 Z M -0.5 12.5 l 13 -13 l 6 0 l -13 13 Z M 11.5 12.5 l 13 -13 l 6 0 l -13 13 Z"></path>
              </g>
            </symbol>
            <pattern
              id="crawlingPattern-818f641a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#818f641a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-5a87971a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#5a87971a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-65927c1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#65927c1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-0000001b"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#0000001b"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-00000024"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#00000024"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-5f5f5f1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#5f5f5f1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
          </defs>
          <g transform="translate(26, 50)">
            <g stroke="none" fill="#E8D9D4">
              <path d="M 34 242L 490 242C 490 235.3726 495.373 230 502 230L 22 230C 28.6274 230 34 235.3726 34 242ZM 502 206L 22 206L 22 230L 502 230L 502 206ZM 22 506L 502 506C 502 512.6274 507.373 518 514 518L 10 518C 16.6274 518 22 512.6274 22 506ZM 514 542L 10 542L 10 518L 514 518L 514 542ZM 262 10L 514 180.264L 514 206L 10 206L 10 180.264L 262 10Z"></path>
            </g>
            <g stroke="none" fill="url(#crawlingPattern-8787871a)">
              <path d="M 34 242L 490 242C 490 235.3726 495.373 230 502 230L 22 230C 28.6274 230 34 235.3726 34 242ZM 502 206L 22 206L 22 230L 502 230L 502 206ZM 22 506L 502 506C 502 512.6274 507.373 518 514 518L 10 518C 16.6274 518 22 512.6274 22 506ZM 514 542L 10 542L 10 518L 514 518L 514 542ZM 262 10L 514 180.264L 514 206L 10 206L 10 180.264L 262 10Z"></path>
            </g>
            <g
              fill="none"
              stroke={
                content.steps[0].color ||
                content.genericColors[0 % content.genericColors.length] ||
                "#6D4C41"
              }
              strokeWidth="2"
            >
              <path d="M 34 242L 490 242C 490 235.3726 495.3726 230 502 230L 22 230C 28.6274 230 34 235.3726 34 242ZM 502 206L 22 206L 22 230L 502 230L 502 206ZM 22 506L 502 506C 502 512.6274 507.3726 518 514 518L 10 518C 16.6274 518 22 512.6274 22 506ZM 514 542L 10 542L 10 518L 514 518L 514 542ZM 262 10L 514 180.2642L 514 206L 10 206L 10 180.2642L 262 10Z"></path>
            </g>
            <g
              fill="none"
              stroke={`${content.steps[0].color || content.genericColors[0 % content.genericColors.length] || "#6D4C41"}80`}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 34 242L 490 242C 490 235.3726 495.3726 230 502 230L 22 230C 28.6274 230 34 235.3726 34 242ZM 502 206L 22 206L 22 230L 502 230L 502 206ZM 22 506L 502 506C 502 512.6274 507.3726 518 514 518L 10 518C 16.6274 518 22 512.6274 22 506ZM 514 542L 10 542L 10 518L 514 518L 514 542ZM 262 10L 514 180.2642L 514 206L 10 206L 10 180.2642L 262 10Z"></path>
            </g>
          </g>
          <g transform="translate(70, 78)">
            <g stroke="none" fill="#E8D9D4">
              <path d="M 217.645 10L 10 154L 426 154L 217.645 10Z"></path>
            </g>
            <g stroke="none" fill="url(#crawlingPattern-8787871a)">
              <path d="M 217.645 10L 10 154L 426 154L 217.645 10Z"></path>
            </g>
            <g
              fill="none"
              stroke={
                content.steps[0].color ||
                content.genericColors[0 % content.genericColors.length] ||
                "#6D4C41"
              }
              strokeWidth="2"
            >
              <path d="M 217.6449 10L 10 154L 426 154L 217.6449 10Z"></path>
            </g>
            <g
              fill="none"
              stroke={`${content.steps[0].color || content.genericColors[0 % content.genericColors.length] || "#6D4C41"}80`}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 217.6449 10L 10 154L 426 154L 217.6449 10Z"></path>
            </g>
          </g>
          {eventData.map((data) => (
            <g key={`event-${data.index}`}>
              <g transform={data.shapeTransform}>
                <g stroke="none" fill={data.shapeFill}>
                  <path d="M 34 58.058594L 106 58.058594L 118 226.058594L 22 226.058594L 34 58.058594ZM 22 10L 118 10L 118 34.0583L 22 34.0583L 22 10ZM 10 274.058594L 130 274.058594L 130 250.058777L 10 250.058777L 10 274.058594ZM 22 34.058594L 118 34.058594C 111.3726 34.058594 106 39.431194 106 46.058594L 34 46.058594C 34 39.431194 28.6274 34.058594 22 34.058594ZM 10 250.058594L 130 250.058594C 123.373 250.058594 118 244.685994 118 238.058594L 22 238.058594C 22 244.685994 16.6274 250.058594 10 250.058594Z"></path>
                  <path d="M 37 46.058594L 103 46.058594C 103 46.058594 115 46.058594 115 52.058594L 115 52.058594C 115 52.058594 115 58.058594 103 58.058594L 37 58.058594C 37 58.058594 25 58.058594 25 52.058594L 25 52.058594C 25 52.058594 25 46.058594 37 46.058594M 28 226.058594L 112 226.058594C 112 226.058594 124 226.058594 124 232.058594L 124 232.058594C 124 232.058594 124 238.058594 112 238.058594L 28 238.058594C 28 238.058594 16 238.058594 16 232.058594L 16 232.058594C 16 232.058594 16 226.058594 28 226.058594"></path>
                </g>
                <g stroke="none" fill={data.shapeFillOverlay}>
                  <path d="M 34 58.058594L 106 58.058594L 118 226.058594L 22 226.058594L 34 58.058594ZM 22 10L 118 10L 118 34.0583L 22 34.0583L 22 10ZM 10 274.058594L 130 274.058594L 130 250.058777L 10 250.058777L 10 274.058594ZM 22 34.058594L 118 34.058594C 111.3726 34.058594 106 39.431194 106 46.058594L 34 46.058594C 34 39.431194 28.6274 34.058594 22 34.058594ZM 10 250.058594L 130 250.058594C 123.373 250.058594 118 244.685994 118 238.058594L 22 238.058594C 22 244.685994 16.6274 250.058594 10 250.058594Z"></path>
                  <path d="M 37 46.058594L 103 46.058594C 103 46.058594 115 46.058594 115 52.058594L 115 52.058594C 115 52.058594 115 58.058594 103 58.058594L 37 58.058594C 37 58.058594 25 58.058594 25 52.058594L 25 52.058594C 25 52.058594 25 46.058594 37 46.058594M 28 226.058594L 112 226.058594C 112 226.058594 124 226.058594 124 232.058594L 124 232.058594C 124 232.058594 124 238.058594 112 238.058594L 28 238.058594C 28 238.058594 16 238.058594 16 232.058594L 16 232.058594C 16 232.058594 16 226.058594 28 226.058594"></path>
                </g>
                <g fill="none" stroke={data.iconStroke} strokeWidth="2">
                  <path d="M 34 58.058594L 106 58.058594L 118 226.058594L 22 226.058594L 34 58.058594ZM 22 10L 118 10L 118 34.0583L 22 34.0583L 22 10ZM 10 250.058777L 130 250.058777L 130 274.058594L 10 274.058594L 10 250.058777ZM 22 34.058594L 118 34.058594C 111.3726 34.058594 106 39.431194 106 46.058594L 34 46.058594C 34 39.431194 28.6274 34.058594 22 34.058594ZM 10 250.058594L 130 250.058594C 123.3726 250.058594 118 244.685994 118 238.058594L 22 238.058594C 22 244.685994 16.6274 250.058594 10 250.058594Z"></path>
                  <path d="M 37 46.058594L 103 46.058594C 103 46.058594 115 46.058594 115 52.058594L 115 52.058594C 115 52.058594 115 58.058594 103 58.058594L 37 58.058594C 37 58.058594 25 58.058594 25 52.058594L 25 52.058594C 25 52.058594 25 46.058594 37 46.058594M 28 226.058594L 112 226.058594C 112 226.058594 124 226.058594 124 232.058594L 124 232.058594C 124 232.058594 124 238.058594 112 238.058594L 28 238.058594C 28 238.058594 16 238.058594 16 232.058594L 16 232.058594C 16 226.058594 16 226.058594 28 226.058594"></path>
                </g>
                <g
                  fill="none"
                  stroke={data.iconStrokeOverlay}
                  strokeWidth="2"
                  strokeDasharray="4,6"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    values="30;0"
                    dur="1s"
                    repeatCount="indefinite"
                    begin="0"
                  ></animate>
                  <path d="M 34 58.058594L 106 58.058594L 118 226.058594L 22 226.058594L 34 58.058594ZM 22 10L 118 10L 118 34.0583L 22 34.0583L 22 10ZM 10 250.058777L 130 250.058777L 130 274.058594L 10 274.058594L 10 250.058777ZM 22 34.058594L 118 34.058594C 111.3726 34.058594 106 39.431194 106 46.058594L 34 46.058594C 34 39.431194 28.6274 34.058594 22 34.058594ZM 10 250.058594L 130 250.058594C 123.3726 250.058594 118 244.685994 118 238.058594L 22 238.058594C 22 244.685994 16.6274 250.058594 10 250.058594Z"></path>
                  <path d="M 37 46.058594L 103 46.058594C 103 46.058594 115 46.058594 115 52.058594L 115 52.058594C 115 52.058594 115 58.058594 103 58.058594L 37 58.058594C 37 58.058594 25 58.058594 25 52.058594L 25 52.058594C 25 52.058594 25 46.058594 37 46.058594M 28 226.058594L 112 226.058594C 112 226.058594 124 226.058594 124 232.058594L 124 232.058594C 124 232.058594 124 238.058594 112 238.058594L 28 238.058594C 28 238.058594 16 238.058594 16 232.058594L 16 232.058594C 16 226.058594 16 226.058594 28 226.058594"></path>
                </g>
              </g>
              <g transform={data.iconTransform}>
                <foreignObject x="30" y="120" width="68" height="68">
                  <div
                    style={{
                      width: "68px",
                      height: "68px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        content.steps[data.index].color ||
                        content.genericColors[
                          data.index % content.genericColors.length
                        ] ||
                        "#9CA3AF",
                      borderRadius: "50%",
                    }}
                    aria-label={
                      content.steps[data.index].icon_description ||
                      content.steps[data.index].title
                    }
                  >
                    <NetworkIcon
                      value={
                        content.steps[data.index].icon_description ??
                        content.steps[data.index].iconId
                      }
                      type="LUCIDE"
                      style={{
                        width: "32px",
                        height: "32px",
                        color: "#ffffff",
                      }}
                    />
                  </div>
                </foreignObject>
              </g>
              <g transform={data.textTransform}>
                <foreignObject x="17.5" y="0" width="150" height="100">
                  {editingIndex === data.index && editingField === "title" ? (
                    <input
                      autoFocus
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      onBlur={() => {
                        setEditingIndex(null);
                        setEditingField(null);
                        if (tempValue !== content.steps[data.index].title) {
                          handleEventUpdate(data.index, { title: tempValue });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.currentTarget.blur();
                        }
                      }}
                      style={{
                        font: "bold 16px Roboto, sans-serif",
                        color: data.fill,
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        width: "100%",
                        padding: "0",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        font: "bold 16px Roboto, sans-serif",
                        color: data.fill,
                        cursor: "text",
                        padding: "0",
                      }}
                      onClick={() => {
                        setEditingIndex(data.index);
                        setEditingField("title");
                        setTempValue(content.steps[data.index].title);
                      }}
                    >
                      {content.steps[data.index].title}
                    </div>
                  )}
                  {editingIndex === data.index &&
                  editingField === "description" ? (
                    <textarea
                      autoFocus
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      onBlur={() => {
                        setEditingIndex(null);
                        setEditingField(null);
                        if (
                          tempValue !== content.steps[data.index].description
                        ) {
                          handleEventUpdate(data.index, {
                            description: tempValue,
                          });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.height = "auto";
                        e.currentTarget.style.height =
                          e.currentTarget.scrollHeight + "px";
                      }}
                      style={{
                        font: "12px Roboto, sans-serif",
                        color: data.descColor,
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        width: "100%",
                        resize: "none",
                        padding: "0",
                        marginTop: "6px",
                      }}
                      rows={2}
                    />
                  ) : (
                    <div
                      style={{
                        font: "12px Roboto, sans-serif",
                        color: data.descColor,
                        cursor: "text",
                        padding: "0",
                        whiteSpace: "pre-wrap",
                        marginTop: "6px",
                      }}
                      onClick={() => {
                        setEditingIndex(data.index);
                        setEditingField("description");
                        setTempValue(content.steps[data.index].description);
                      }}
                    >
                      {content.steps[data.index].description}
                    </div>
                  )}
                </foreignObject>
              </g>
            </g>
          ))}
          <g transform="translate(206, 162)">
            <g
              stroke="none"
              fill={
                content.steps[0].color ||
                content.genericColors[0 % content.genericColors.length] ||
                "#6D4C41"
              }
            >
              <text
                style={{ font: "20px Roboto, sans-serif", whiteSpace: "pre" }}
              >
                <tspan x="17.09" y="33.5" dominant-baseline="ideographic">
                  {content.title}
                </tspan>
              </text>
            </g>
          </g>
        </svg>
        {extraEvents.length > 0 && (
          <div className="mt-4">
            {extraEvents.map((event) => (
              <motion.div
                key={`extra-event-${event.index}`}
                className="flex flex-col items-center p-4"
                variants={itemVariants}
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-2 shadow-lg"
                  style={{
                    backgroundColor: event.color,
                  }}
                  aria-label={event.iconDescription || event.title}
                >
                  <NetworkIcon
                    value={event.iconDescription ?? event.iconId}
                    type="LUCIDE"
                    className="w-8 h-8"
                  />
                </div>
                {editingIndex === event.index && editingField === "title" ? (
                  <input
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingIndex(null);
                      setEditingField(null);
                      if (tempValue !== event.title) {
                        handleEventUpdate(event.index, { title: tempValue });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      }
                    }}
                    className="font-semibold h-10 flex items-center focus:outline-none focus:ring-2 focus:ring-primary bg-transparent rounded px-1"
                    style={{ color: event.color }}
                  />
                ) : (
                  <h4
                    className="font-semibold h-10 flex items-center focus:outline-none focus:ring-2 focus:ring-primary focus:bg-gray-100 rounded px-1 cursor-text"
                    style={{ color: event.color }}
                    onClick={() => {
                      setEditingIndex(event.index);
                      setEditingField("title");
                      setTempValue(event.title);
                    }}
                  >
                    {event.title}
                  </h4>
                )}
                {editingIndex === event.index &&
                editingField === "description" ? (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingIndex(null);
                      setEditingField(null);
                      if (tempValue !== event.description) {
                        handleEventUpdate(event.index, {
                          description: tempValue,
                        });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.height = "auto";
                      e.currentTarget.style.height =
                        e.currentTarget.scrollHeight + "px";
                    }}
                    rows={2}
                    className="w-full focus:outline-none focus:ring-2 focus:ring-primary bg-transparent rounded px-1 text-center text-sm"
                    style={{ color: event.descColor }}
                  />
                ) : (
                  <p
                    className="focus:outline-none focus:ring-2 focus:ring-primary focus:bg-gray-100 rounded px-1 cursor-text text-center text-sm"
                    style={{ color: event.descColor }}
                    onClick={() => {
                      setEditingIndex(event.index);
                      setEditingField("description");
                      setTempValue(event.description);
                    }}
                  >
                    {event.description}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
